'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface PillTabOption<T extends string = string> {
  value: T;
  label: string;
}

interface PillTabsProps<T extends string = string> {
  options: PillTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  layoutId: string;
  className?: string;
  buttonClassName?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  indicatorClassName?: string;
}

export function PillTabs<T extends string = string>({
  options,
  value,
  onChange,
  layoutId,
  className,
  buttonClassName,
  activeClassName = 'text-white',
  inactiveClassName = 'text-black/74',
  indicatorClassName = 'bg-black',
}: PillTabsProps<T>) {
  return (
    <div
      className={cn(
        'inline-flex items-center rounded-[6.875rem] border border-black/10 bg-black/[0.03] px-[0.75rem] py-[0.75rem]',
        className,
      )}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'relative rounded-[6.875rem] px-[2rem] py-[1.5rem] text-[2.25rem] font-medium leading-none tracking-[-0.045rem] transition-colors duration-300',
              isActive ? activeClassName : inactiveClassName,
              buttonClassName,
            )}
          >
            {isActive && (
              <motion.span
                layoutId={layoutId}
                className={cn('absolute inset-0 rounded-[6.875rem]', indicatorClassName)}
                transition={{ duration: 0.28, ease }}
              />
            )}
            <span className="relative z-10">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
